import { X } from "lucide-react";
import type { PlaylistEntry, ValidationIssue } from "@protv-converter/core";

interface EntryEditorProps {
  entry: PlaylistEntry;
  issues: ValidationIssue[];
  onChange: (patch: Partial<PlaylistEntry>) => void;
  onClose: () => void;
}

export function EntryEditor({ entry, issues, onChange, onClose }: EntryEditorProps) {
  return (
    <div className="border-y border-accent/45 bg-accent-soft px-4 py-4 sm:px-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow text-accent">Edit entry</p>
          <h3 className="mt-1 font-semibold text-ink">{entry.title || "Untitled media"}</h3>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close entry editor"><X aria-hidden="true" size={17} /></button>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div>
          <label className="field-label" htmlFor={`main-${entry.id}`}>Main URL</label>
          <input aria-label="Main URL" id={`main-${entry.id}`} className="field" value={entry.mainUrl} onChange={(event) => onChange({ mainUrl: event.target.value })} />
        </div>
        <div>
          <label className="field-label" htmlFor={`alternate-${entry.id}`}>Alternate URL</label>
          <input aria-label="Alternate URL" id={`alternate-${entry.id}`} className="field" value={entry.alternateUrl} onChange={(event) => onChange({ alternateUrl: event.target.value })} placeholder="Optional Quest or fallback URL" />
        </div>
        <div>
          <label className="field-label" htmlFor={`title-${entry.id}`}>Title</label>
          <input aria-label="Title" id={`title-${entry.id}`} className="field" value={entry.title} onChange={(event) => onChange({ title: event.target.value })} />
        </div>
        <div>
          <label className="field-label" htmlFor={`tags-${entry.id}`}>Tags</label>
          <input aria-label="Tags" id={`tags-${entry.id}`} className="field" value={entry.tags.join(", ")} onChange={(event) => onChange({ tags: event.target.value.split(",").map((tag) => tag.trim()).filter(Boolean) })} placeholder="music, ambient, 2020s" />
        </div>
        <div className="xl:col-span-2">
          <label className="field-label" htmlFor={`description-${entry.id}`}>Description</label>
          <textarea aria-label="Description" id={`description-${entry.id}`} className="field min-h-20 resize-y" value={entry.description} onChange={(event) => onChange({ description: event.target.value })} placeholder="Optional in ProTV 3.x" />
        </div>
        <div className="xl:col-span-2">
          <label className="field-label" htmlFor={`image-${entry.id}`}>Image path</label>
          <input aria-label="Image path" id={`image-${entry.id}`} className="field" value={entry.imagePath} onChange={(event) => onChange({ imagePath: event.target.value.replace(/^\/+/, "") })} placeholder="Assets/ProTV/PlaylistImages/poster.jpg" />
          <p className="field-help">Use a path relative to the Unity project root. The target texture must import as Sprite (2D and UI).</p>
        </div>
      </div>

      {issues.length > 0 ? (
        <ul className="mt-4 space-y-1.5 border-t border-accent/25 pt-3 text-xs">
          {issues.map((issue) => <li className={issue.level === "error" ? "text-danger" : "text-warning"} key={`${issue.level}-${issue.message}`}>{issue.message}</li>)}
        </ul>
      ) : null}
    </div>
  );
}
